import { MemberTypes } from "@custom-types/member-types";
import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { BaseEntityAdapter } from "@store/store-types";
import { getMemberId } from "@utils/member-utils";
import { ActiveMembersState } from "@store/active-members/active-members-slice-helper";
import { fetchActiveMembers } from "@store/active-members/active-members-slice-thunk";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";

/**
 * Creates an entity adapter to store a map with all active members that counted in the plan.
 */
export const activeMembersAdapter: BaseEntityAdapter<MemberTypes> =
  createEntityAdapter({
    selectId: (member) => getMemberId(member),
  });

const initialState: ActiveMembersState = {
  ...activeMembersAdapter.getInitialState(),
  fetching: {
    status: "uninitialized",
  },
};

/**
 * Slice to access state of loaded active members
 */
const activeMembersSlice = createSlice({
  name: "activeMembers",
  initialState,
  reducers: {
    /**
     * Accepts a single active member entity and adds or replaces it.
     *
     * @param state store state
     * @param entity Project to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setOne: activeMembersAdapter.setOne,
    /**
     * Accepts an array of member entities, and adds or replaces them.
     *
     * @param state store state
     * @param entity Projects to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setMany: activeMembersAdapter.setMany,
    /**
     * Removes all member entities from the store.
     *
     * @param state store state
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeAll: activeMembersAdapter.removeAll,
    /**
     * Accepts an array of member IDs, and removes each member entity with those IDs if they exist.
     *
     * @param state store state
     * @param entity Project Ids to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeMany: activeMembersAdapter.removeMany,
    /**
     * Accepts a single member IDs, and removes the member entity with that ID if it exists.
     *
     * @param state store state
     * @param entity Project Id to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeOne: activeMembersAdapter.removeOne,
    resetActiveMembersState: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchActiveMembers.pending, (state) => {
        state.fetching.status = "pending";
      })
      .addCase(fetchActiveMembers.fulfilled, (state, action) => {
        activeMembersAdapter.setAll(state, action.payload);

        state.fetching.status = "succeeded";
      })
      .addCase(fetchActiveMembers.rejected, (state) => {
        state.fetching.status = "rejected";
      })

      .addCase(removeMemberFromCompany.fulfilled, (state, action) => {
        activeMembersAdapter.removeOne(state, action.payload.identity);
      });
  },
});

export const {
  setOne,
  setMany,
  removeAll,
  removeMany,
  removeOne,
  resetActiveMembersState,
} = activeMembersSlice.actions;

export const activeMembersReducer = activeMembersSlice.reducer;
